/**
 * Application settings and features - per APP_ID per deployment
 * Production object is the DEFAULT
 * If staging exists it will overwrite defaults
 *
 * READ THE DOCS BEFORE ADDING A FEATURE
 * DON'T FORGET TO ALSO ADD NEW FEATURES TO THE DOCS
 *
 */

import {
  KIND_CAPITALISATION,
  KIND_LIFE_INSURANCE,
  KIND_MARKET,
  KIND_PEA,
  KIND_PER
} from './src/constants/contract-kind'
import {
  ADVISORY_MANAGEMENT,
  FREE_MANAGEMENT,
  MIXED_MANAGEMENT,
  PATRIMONY_DECISION
} from './src/constants/management-type'

const CONNECTOR_SLUG_CREDIT_AGRICOLE = 'credit-agricole'

const frenchNonWorkingDates = [
  `01-01`, // nouvel an
  `01-05`, // fête du travail
  `08-05`, // fête de la Victoire 1945
  `14-07`, // fête Nationale
  `15-08`, // l'assomption
  `01-11`, // la Toussaint
  `11-11`, // l'armistice 1918
  `25-12` // Noël
]

const enableIndicatorsAtStepConfig = {
  cabp: [1, 2, 3],
  vieplus: [2]
}

const config = {
  cabp: {
    params: {
      appId: 'cabp',
      defaultLocale: 'fr-FR',
      logoAdvisory: {
        img: 'logo.svg',
        style: ['width: 80%']
      },
      logo: {
        img: 'logo.svg',
        style: ['width: 80%']
      },
      logoPdfArbitration: {
        img: 'logo-cabp.png',
        style: ['width: 30mm']
      },
      logoPdfReporting: {
        img: 'logo-white.svg',
        style: []
      },
      logoLobby: {
        img: 'logo-lobby.svg',
        style: ['padding: 16px', 'border-radius: 4px']
      }
    },
    features: {
      production: {
        aggregationType: 'webview',
        allowUserResetPassword: false,
        allowUserRegistration: false,
        apolloIsDown: false,
        arbitrableContracts: [
          KIND_LIFE_INSURANCE,
          KIND_CAPITALISATION,
          KIND_PEA,
          KIND_MARKET
        ],
        arbitrableManagementTypes: [ADVISORY_MANAGEMENT, PATRIMONY_DECISION],
        columnInvestmentsTable: [
          'name',
          'isin',
          'parts',
          'value',
          'amount',
          'weight',
          'perf-ytd'
        ],
        customerListRiskKey: 'riskRespected',
        disablePerformances: false,
        dashboardUserNameDetailed: false,
        enableAvgRate: false,
        enableCaptchaForLogin: false,
        enableConseil: true,
        enableCookieConsent: false,
        enableCompleteYourProfile: false,
        enableEsgSettings: false,
        enableLogoutUrl: true,
        enableMaintenancePage: false,
        enableMorningStarIndicators: true,
        enablePasswordReset: false,
        enableWelcomePage: false,
        enableRedirectionWelcomePage: false,
        enablePeaIndicators: true,
        enablePerformanceCalculation: true,
        enableRegister: false,
        enableSmsVerification: false,
        euroFundLivretAComparison: false,
        forceSmsVerification: false,
        nonWorkingDays: [6, 0], // disable saturday 6 and sunday 0
        nonWorkingDates: frenchNonWorkingDates,
        realEstate: true,
        realEstateEstimator: true,
        routesToDisableIntro: [
          'accounts-new-index',
          'accounts-new-form',
          'accounts-new-list'
        ],
        mainLogin: 'cabp',
        maximumRiskProfile: 4,
        secondLogin: false,
        sinceStartDate: '2017-01-01',
        stockExchangeVisible: true,
        typeConformityControl: 'apollo',
        userAccountsFree: true,
        validatedCguRedirection: CONNECTOR_SLUG_CREDIT_AGRICOLE,
        wealthDecisionVisible: true,
        wealthDecisionDisabled: false,
        worldmapRepartitionDisplay: false,
        aAllocationIndicatorsVisible: true,
        aAssistanceUrl: false,
        aAutomaticRiskCalculationModelPortfolio: false,
        aClientView: true,
        aProductCustomerArbitrationsCategories: [
          'age',
          'risk',
          'wealthAmount',
          'kinds',
          'portfolioTemplate'
        ],
        aCustomersListFiltersCategories: [
          'onlyCustomersOfAdvisor',
          'risk',
          'wealthAmount',
          'kinds',
          'portfoliosBanks',
          'advisors'
        ],
        aCustomersSidebarFiltersPortfoliosKindsKey: 'portfoliosKinds',
        aCustomerVisionCguMandatory: true,
        aDisableCaAmount: false,
        aDisableCustomerView: false,
        aDisableModelPortfolioInAllocationRecommendation: false,
        aDurabilityColumn: false,
        aEnableAllocationFeesStep: false,
        aEnableCustomerVerification: false,
        aEnableAllocationFundDetails: false,
        aEnableArbitrationColumnsModelPortfolio: ['isin', 'name', 'share'],
        aEnableArbitrations: true,
        aEnableBlockRepartition: true,
        aCustomerListCard: false,
        aEnableClientInfoArbitration: false,
        aEnableCompliance: false,
        aEnableComplianceTabs: [],
        aEnablemultipleArbitrageFlowOptions: [1, 2],
        aEnableFeesAnnexes: true,
        aEnableFinancialWeightArbitrationTable: true,
        aEnableIsinDetail: false,
        aEnableIndicatorsAtStep: enableIndicatorsAtStepConfig.cabp,
        aEnableModelPortfolioModal: false,
        aEnablePerfYtdColumn: true,
        aEnablePortfolioBuilder: false,
        aEnableProductCustomerArbitrations: false,
        aEnableRecommendationModelPortfolioSameAllocation: true,
        aEnableRecommendationOverviewSameAllocation: true,
        aEnableRecommendationTableHeader: false,
        aEnableRiskWarning: true,
        aEnableSimpleArbitration: true,
        aEnableStatuQuo: true,
        aEnableSupport: true,
        aEnableBankConcept: true,
        aEnableEsgWarningModal: false,
        aFaqUrl: false,
        aIndicatorsOrder: [
          'account-unit-total',
          'account-unit-arbitrable',
          'delta-action'
        ],
        aInteractions: true,
        aInviteClients: false,
        aInternalAmount: 'creditAgricoleAmount',
        aMandateManagement: false,
        aManualInternalManagement: false,
        aMissions: false,
        aNewAppointmentAfterArbitration: false,
        aPreferUserId: false,
        aPropertyRiskPortfolio: 'currentRisk',
        aCheckingAmount: true,
        aShowAssetColor: true,
        aShowAssetTypeInFundsList: true,
        aShowClientRisk: false,
        aShowEsgOnCustomerPage: false,
        aShowLastInteractionAtTable: true,
        aShowMifDate: true,
        aShowReco: false,
        aShowSignUpDateFilter: false,
        aShowArbitrationDropdown: true,
        aShowArbitrationJustification: false,
        aShowArbitrationStatus: true,
        aShowPreIntroPdf: true,
        aShowInternalHeritage: true,
        aShowLifeInsurance: true,
        aShowLastAggregationFilter: true,
        aShowRiskOnCustomerPage: false,
        aShowBankName: true,
        aShowSinceColumnPdf: true,
        aShowStockExposurePdf: true,
        aShowContractIdInArbitrationsTable: false,
        aShowFundsFilter: false,
        aShowOrientationSupportInFundsList: false,
        aSidebarFiltersPortfoliosModelsKey: 'modelPortfolio',
        aSidebarFiltersPortfoliosKindsKey: 'portfolioKinds',
        aSidebarFiltersPortfoliosKindsValues: [
          'lifeinsurance',
          'checking',
          'savings',
          'market',
          'loan',
          'pee',
          'perco',
          'article83',
          'rsp',
          'pea',
          'capitalisation',
          'perp',
          'madelin',
          'unknown',
          'other'
        ],

        riskMapping: {},

        pdfArbitrationGeneralWarnings: true,
        pdfArbitrationAnnexes: true,
        pdfArbitrationClickableDic: false,
        pdfArbitrationTypeIdInTitle: 'externalProductId',

        cEnableMyLoans: true,
        cEnableDownloadCsv: true,
        cEnableMyAdvice: true,
        cEnableHoldingsDashboardKinds: [
          'checking',
          'savings',
          'lifeinsurance',
          'peaMarket',
          'realestate'
        ],
        cAssistanceUrl: false,
        cDebtRatio: false,
        cShowDocumentsCgu: true,
        cShowKindPreferred: true,
        cShowAccountsRepartition: true,
        cShowMangementReport: true,
        cShowMyProductConfiguration: false,
        cShowNewStyleModal: false,
        cVideoLinkAdviceDashboard: "https://vimeo.com/1145985754/6b7cb08554"
      },
      staging: {
        realEstate: true
      }
    }
  },
  fundchannel: {
    params: {
      appId: 'fundchannel',
      defaultLocale: 'fr-FR',
      logoAdvisory: {
        img: 'logo.png',
        style: ['width: 85%']
      },
      logo: {
        img: 'logo.png',
        style: ['filter: brightness(10)', 'width: 85%']
      },
      logoPdfArbitration: {
        img: 'citibank.png',
        style: ['width: 50mm']
      },
      logoPdfReporting: {
        img: 'logo.png',
        style: []
      },
      logoLobby: {
        img: '',
        style: []
      }
    },
    features: {
      production: {
        aggregationType: 'webview',
        allowUserResetPassword: false,
        allowUserRegistration: false,
        apolloIsDown: false,
        arbitrableContracts: [
          KIND_LIFE_INSURANCE,
          KIND_CAPITALISATION,
          KIND_PEA,
          KIND_MARKET
        ],
        arbitrableManagementTypes: [ADVISORY_MANAGEMENT, PATRIMONY_DECISION],
        columnInvestmentsTable: [
          'name',
          'isin',
          'parts',
          'value',
          'amount',
          'weight',
          'management-fees',
          'scoring'
        ],
        disablePerformances: false,
        dashboardUserNameDetailed: false,
        enableAvgRate: false,
        enableCaptchaForLogin: false,
        enableConseil: true,
        enableCookieConsent: false,
        enableCompleteYourProfile: false,
        enableEsgSettings: false,
        enableLogoutUrl: false,
        enableMaintenancePage: false,
        enableMorningStarIndicators: false,
        enablePasswordReset: false,
        enableWelcomePage: false,
        enableRedirectionWelcomePage: false,
        enablePeaIndicators: false,
        enablePerformanceCalculation: true,
        enableRegister: false,
        enableSmsVerification: false,
        euroFundLivretAComparison: false,
        forceSmsVerification: false,
        nonWorkingDays: [6, 0], // disable saturday 6 and sunday 0
        nonWorkingDates: frenchNonWorkingDates,
        realEstate: true,
        realEstateEstimator: true,
        routesToDisableIntro: [
          'accounts-new-index',
          'accounts-new-form',
          'accounts-new-list'
        ],
        mainLogin: 'default',
        maximumRiskProfile: 4,
        secondLogin: false,
        stockExchangeVisible: true,
        typeConformityControl: '',
        userAccountsFree: true,
        validatedCguRedirection: CONNECTOR_SLUG_CREDIT_AGRICOLE,
        wealthDecisionVisible: true,
        wealthDecisionDisabled: false,
        worldmapRepartitionDisplay: true,
        aAllocationIndicatorsVisible: true,
        aAssistanceUrl: false,
        aAutomaticRiskCalculationModelPortfolio: false,
        aClientView: true,
        aProductCustomerArbitrationsCategories: [
          'age',
          'risk',
          'wealthAmount',
          'kinds',
          'portfolioTemplate'
        ],
        aCustomersListFiltersCategories: [
          'onlyCustomersOfAdvisor',
          'riskRespected',
          'wealthAmount',
          'kinds',
          'portfoliosBanks',
          'advisors'
        ],
        aCustomerVisionCguMandatory: false,
        aDisableCaAmount: false,
        aDisableCustomerView: false,
        aDisableModelPortfolioInAllocationRecommendation: false,
        aDurabilityColumn: false,
        aEnableAllocationFeesStep: false,
        aEnableCustomerVerification: false,
        aEnableAllocationFundDetails: false,
        aEnableArbitrationColumnsModelPortfolio: ['isin', 'name', 'share'],
        aEnableArbitrations: true,
        aEnableBlockRepartition: true,
        aCustomerListCard: false,
        aEnableClientInfoArbitration: false,
        aEnableCompliance: false,
        aEnableComplianceTabs: [],
        aEnableFeesAnnexes: true,
        aEnableFinancialWeightArbitrationTable: true,
        aEnableIndicatorsAtStep: enableIndicatorsAtStepConfig.cabp,
        aEnableIsinDetail: false,
        aEnableModelPortfolioModal: false,
        aEnablePerfYtdColumn: false,
        aEnablePortfolioBuilder: false,
        aEnableProductCustomerArbitrations: false,
        aEnableRecommendationModelPortfolioSameAllocation: true,
        aEnableRecommendationOverviewSameAllocation: true,
        aEnableRecommendationTableHeader: false,
        aEnableRiskWarning: true,
        aEnableSimpleArbitration: true,
        aEnableStatuQuo: true,
        aEnableSupport: true,
        aEnableBankConcept: true,
        aEnableEsgWarningModal: false,
        aFaqUrl: false,
        aIndicatorsOrder: [
          'account-unit-total',
          'account-unit-arbitrable',
          'delta-action'
        ],
        aEnablemultipleArbitrageFlowOptions: [1, 2],
        aInteractions: true,
        aInviteClients: false,
        aInternalAmount: '',
        aMandateManagement: false,
        aManualInternalManagement: false,
        aMissions: false,
        aNewAppointmentAfterArbitration: true,
        aPreferUserId: false,
        aPropertyRiskPortfolio: 'currentRisk',
        aCheckingAmount: false,
        aShowAssetColor: true,
        aShowAssetTypeInFundsList: false,
        aShowClientRisk: false,
        aShowEsgOnCustomerPage: false,
        aShowLastInteractionAtTable: true,
        aShowMifDate: true,
        aShowReco: false,
        aShowSignUpDateFilter: false,
        aShowArbitrationDropdown: false,
        aShowArbitrationJustification: false,
        aShowArbitrationStatus: true,
        aShowPreIntroPdf: true,
        aShowInternalHeritage: true,
        aShowLifeInsurance: true,
        aShowLastAggregationFilter: true,
        aShowRiskOnCustomerPage: false,
        aShowBankName: true,
        aShowSinceColumnPdf: true,
        aShowStockExposurePdf: true,
        aShowContractIdInArbitrationsTable: false,
        aShowFundsFilter: false,
        aShowOrientationSupportInFundsList: false,

        riskMapping: {},

        pdfArbitrationGeneralWarnings: true,
        pdfArbitrationAnnexes: true,
        pdfArbitrationClickableDic: false,
        pdfArbitrationTypeIdInTitle: 'externalProductId',

        cEnableMyLoans: true,
        cEnableDownloadCsv: true,
        cEnableMyAdvice: true,
        cEnableHoldingsDashboardKinds: [
          'checking',
          'savings',
          'lifeinsurance',
          'peaMarket',
          'realestate'
        ],
        cAssistanceUrl: false,
        cDebtRatio: false,
        cShowDocumentsCgu: true,
        cShowKindPreferred: true,
        cShowAccountsRepartition: true,
        cShowMangementReport: true,
        cShowMyProductConfiguration: false,
        cShowNewStyleModal: false,
        cVideoLinkAdviceDashboard: ''
      },
      staging: {
        realEstate: true
      }
    }
  },
  vieplus: {
    params: {
      appId: 'vieplus',
      defaultLocale: 'fr-FR',
      logoAdvisory: {
        img: 'logo.png',
        style: ['width: 55%']
      },
      logo: {
        img: 'logo.png',
        style: ['filter: brightness(10)', 'width: 30%']
      },
      logoPdfArbitration: {
        img: '',
        style: ['width: 50mm']
      },
      logoPdfReporting: {
        img: 'logo.png',
        style: ['height: 50%', 'margin: auto 5px', 'width: inherit']
      },
      logoLobby: {
        img: 'logo.png',
        style: [
          `background-color: ${APP_THEME.palette.primary.main}`,
          'padding: 16px',
          'border-radius: 4px'
        ]
      }
    },
    features: {
      production: {
        aggregationType: 'webview',
        allowUserResetPassword: false,
        allowUserRegistration: false,
        apolloIsDown: true,
        arbitrableContracts: [
          KIND_LIFE_INSURANCE,
          KIND_CAPITALISATION,
          KIND_PEA,
          KIND_MARKET,
          KIND_PER
        ],
        arbitrableManagementTypes: [FREE_MANAGEMENT, MIXED_MANAGEMENT],
        availablePortfoliosRiskKey: 'riskLevel',
        columnInvestmentsTable: [
          'name',
          'isin',
          'parts',
          'value',
          'amount',
          'weight',
          'management-fees'
        ],
        customerListRiskKey: 'riskLevel',
        dashboardUserNameDetailed: true,
        disablePerformances: true,
        enableAvgRate: false,
        enableCaptchaForLogin: true,
        enableConseil: true,
        enableCookieConsent: false,
        enableCompleteYourProfile: false,
        enableEsgSettings: true,
        enableLogoutUrl: false,
        enableMaintenancePage: false,
        enableMorningStarIndicators: false,
        enablePasswordReset: false,
        enableWelcomePage: true,
        enableRedirectionWelcomePage: true,
        enablePeaIndicators: false,
        enablePerformanceCalculation: false,
        enableRegister: false,
        enableSmsVerification: true,
        euroFundLivretAComparison: false,
        forceSmsVerification: false,
        nonWorkingDays: [6, 0], // disable saturday 6 and sunday 0
        nonWorkingDates: frenchNonWorkingDates,
        realEstate: true,
        realEstateEstimator: true,
        routesToDisableIntro: [],
        mainLogin: 'keycloak',
        maximumRiskProfile: 5,
        secondLogin: false,
        sinceStartDate: '2025-01-01',
        stockExchangeVisible: false,
        typeConformityControl: 'sm',
        userAccountsFree: true,
        validatedCguRedirection: '',
        wealthDecisionVisible: false,
        wealthDecisionDisabled: true,
        worldmapRepartitionDisplay: true,
        aAllocationIndicatorsVisible: true,
        aAssistanceUrl: false,
        aAutomaticRiskCalculationModelPortfolio: false,
        aClientView: true,
        aProductCustomerArbitrationsCategories: [
          'age',
          'risk',
          'wealthAmount',
          'kinds',
          'portfolioTemplate',
          'esgProfile',
          'plusOrMinusValue'
        ],
        aCustomersListFiltersCategories: [
          'wealthAmount',
          'kinds',
          'age',
          'risk',
          'esgProfile'
        ],
        aCustomersSidebarFiltersPortfoliosKindsKey: 'portfoliosKinds',
        aCustomerVisionCguMandatory: false,
        aDisableCaAmount: true,
        aDisableCustomerView: false,
        aDisableModelPortfolioInAllocationRecommendation: true,
        aDurabilityColumn: true,
        aEnableAllocationFeesStep: true,
        aEnableCustomerVerification: true,
        aEnableAllocationFundDetails: true,
        aEnableArbitrationColumnsModelPortfolio: [
          'isin',
          'name',
          'durability',
          'price',
          'delta-action',
          'share'
        ],
        aEnableArbitrations: false,
        aEnableBlockRepartition: false,
        aCustomerListCard: true,
        aEnableClientInfoArbitration: true,
        aEnableCompliance: true,
        aEnableComplianceTabs: ['overview', 'risk', 'esg', 'documents'],
        aEnableFeesAnnexes: false,
        aEnableFinancialWeightArbitrationTable: false,
        aEnableIndicatorsAtStep: enableIndicatorsAtStepConfig.vieplus,
        aEnableIsinDetail: true,
        aEnableModelPortfolioModal: true,
        aEnablePerfYtdColumn: false,
        aEnablePortfolioBuilder: true,
        aEnableProductCustomerArbitrations: true,
        aEnableRecommendationModelPortfolioSameAllocation: false,
        aEnableRecommendationOverviewSameAllocation: false,
        aEnableRecommendationTableHeader: true,
        aEnableRiskWarning: false,
        aEnableSimpleArbitration: true,
        aEnableStatuQuo: false,
        aEnableSupport: false,
        aEnableBankConcept: false,
        aEnableEsgWarningModal: true,
        aFaqUrl: false,
        aIndicatorsOrder: [
          'account-unit-total',
          'esg-indicators',
          'delta-risk'
        ],
        aEnablemultipleArbitrageFlowOptions: [1, 2],
        aInteractions: true,
        aInviteClients: false,
        aInternalAmount: '',
        aMandateManagement: true,
        aManualInternalManagement: false,
        aMissions: false,
        aNewAppointmentAfterArbitration: false,
        aPreferUserId: false,
        aPropertyRiskPortfolio: 'recommendedRisk',
        aCheckingAmount: false,
        aShowAssetColor: false,
        aShowAssetTypeInFundsList: false,
        aShowClientRisk: false,
        aShowEsgOnCustomerPage: true,
        aShowLastInteractionAtTable: true,
        aShowMifDate: false,
        aShowReco: false,
        aShowSignUpDateFilter: false,
        aShowArbitrationDropdown: true,
        aShowArbitrationJustification: true,
        aShowArbitrationStatus: false,
        aShowPreIntroPdf: false,
        aShowInternalHeritage: false,
        aShowLifeInsurance: false,
        aShowLastAggregationFilter: false,
        aShowRiskOnCustomerPage: true,
        aShowBankName: false,
        aShowSinceColumnPdf: false,
        aShowStockExposurePdf: false,
        aShowContractIdInArbitrationsTable: true,
        aShowFundsFilter: true,
        aShowOrientationSupportInFundsList: true,
        aSidebarFiltersPortfoliosModelsKey: 'portfolioTemplate',
        aSidebarFiltersPortfoliosKindsKey: 'kinds',
        aSidebarFiltersPortfoliosKindsValues: [
          'lifeinsurance',
          'per',
          'article83',
          'pea',
          'capitalisation',
          'perp',
          'madelin',
          'unknown',
          'other'
        ],
        riskMapping: {
          prudent: 1,
          defensif: 2,
          equilibre: 3,
          offensif: 4,
          agressif: 5
        },

        pdfArbitrationGeneralWarnings: false,
        pdfArbitrationAnnexes: false,
        pdfArbitrationClickableDic: true,
        pdfArbitrationTypeIdInTitle: 'externalId',

        cEnableMyLoans: false,
        cEnableDownloadCsv: false,
        cEnableMyAdvice: false,
        cEnableHoldingsDashboardKinds: [
          'lifeinsurance',
          'capitalisation',
          'per',
          'perp',
          'peaMarket'
        ],
        cAssistanceUrl: false,
        cDebtRatio: false,
        cShowDocumentsCgu: false,
        cShowKindPreferred: false,
        cShowAccountsRepartition: false,
        cShowMangementReport: false,
        cShowMyProductConfiguration: true,
        cShowNewStyleModal: false,
        cVideoLinkAdviceDashboard: ''
      },
      staging: {
        enableCaptchaForLogin: false
      },
      preProduction: {
        aPreferUserId: false,
        enableCaptchaForLogin: false
      }
    }
  }
}

export function featureFlag(feature) {
  return Boolean(featureValue(feature))
}

export function featureValue(feature) {
  const env = process.env.NODE_ENV
  const appId = process.env.APP_ID
  let cfg = config[appId].features

  if ((env === 'staging' || env === 'development') && cfg.staging) {
    cfg = Object.assign({}, cfg.production, cfg.staging)
  } else if (env === 'pre-production' && cfg.preProduction) {
    cfg = Object.assign({}, cfg.production, cfg.preProduction)
  } else {
    cfg = cfg.production
  }

  if (!cfg.hasOwnProperty(feature)) {
    throw new Error(`Feature flag name does not exist - ${feature}`)
  } else {
    return cfg[feature]
  }
}

export default config
